import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { LuQrCode } from 'react-icons/lu';

const { FiWifi, FiInfo } = FiIcons;

function LobbyDisplayContent() {
  const [searchParams] = useSearchParams();
  const studioId = searchParams.get('s');

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeString, setTimeString] = useState('');

  useEffect(() => {
    document.title = "StudioSign | Lobby Welcome";
  }, []);

  useEffect(() => {
    if (!studioId) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from('studio_settings')
        .select('id, studio_name, show_name, show_clock, timezone, lobby_welcome, lobby_subtext, left_card_type, left_card_input_1, left_card_input_2, right_card_type, right_card_input_1, right_card_input_2')
        .eq('id', studioId)
        .single();

      if (data && !error) setSettings(data);
      setLoading(false);
    };

    fetchSettings();

    const channel = supabase
      .channel(`studio_settings_${studioId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'studio_settings', filter: `id=eq.${studioId}` },
        (payload) => { setSettings((prev) => ({ ...prev, ...payload.new })); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [studioId]);

  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        console.error(`${err.name}, ${err.message}`);
      }
    };

    requestWakeLock();

    const handleVisibilityChange = () => {
      if (wakeLock !== null && document.visibilityState === 'visible') requestWakeLock();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock !== null) {
        wakeLock.release();
        wakeLock = null;
      }
    };
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const tz = settings?.timezone;
      const now = new Date();
      try {
        const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true, ...(tz ? { timeZone: tz } : {}) };
        const dateOptions = { weekday: 'short', month: 'short', day: 'numeric', ...(tz ? { timeZone: tz } : {}) };
        const timeStr = now.toLocaleTimeString('en-US', timeOptions);
        const dateStr = now.toLocaleDateString('en-US', dateOptions).toUpperCase();
        setTimeString(`${dateStr} • ${timeStr}`);
      } catch (e) {
        const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
        setTimeString(`${dateStr} • ${timeStr}`);
      }
    };

    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
  }, [settings?.timezone]);

  const handleDoubleTap = async () => {
    if (!document.fullscreenElement) {
      try { await document.documentElement.requestFullscreen(); }
      catch (err) { console.error('Fullscreen request failed:', err); }
    }
  };

  const renderCard = (type, input1, input2) => {
    if (!type || type === 'None') return null;

    if (type === 'Wi-Fi') {
      return (
        <div className="w-[30vw] min-w-[320px] min-h-[180px] bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col transition-opacity duration-700 ease-in-out">
          <SafeIcon icon={FiWifi} className="text-zinc-400 text-2xl mb-6" />
          <div className="mt-auto flex flex-col">
            <div className="text-3xl font-bold text-white leading-tight break-words">{input1}</div>
            {input2 && (
              <div className="text-xl font-medium text-zinc-300 mt-2 leading-tight break-words">{input2}</div>
            )}
          </div>
        </div>
      );
    }

    if (type === 'QR Code') {
      const qrUrl = input2 ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(input2)}` : null;
      return (
        <div className="w-[30vw] min-w-[320px] min-h-[180px] bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col transition-opacity duration-700 ease-in-out">
          <SafeIcon icon={LuQrCode} className="text-zinc-400 text-2xl mb-6" />
          <div className="flex flex-row justify-between items-end gap-6 mt-auto">
            <div className="text-3xl font-bold text-white leading-tight break-words flex-1">{input1}</div>
            <div className="w-24 h-24 bg-white rounded-xl flex-shrink-0 flex items-center justify-center p-1 overflow-hidden">
              {qrUrl ? (
                <img src={qrUrl} alt="QR Code" className="w-full h-full object-contain" />
              ) : (
                <SafeIcon icon={LuQrCode} className="text-zinc-950 text-5xl" />
              )}
            </div>
          </div>
        </div>
      );
    }

    if (type === 'Info') {
      return (
        <div className="w-[30vw] min-w-[320px] min-h-[180px] bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col transition-opacity duration-700 ease-in-out">
          <SafeIcon icon={FiInfo} className="text-zinc-400 text-2xl mb-6" />
          <div className="mt-auto flex flex-col">
            <div className="text-3xl font-bold text-white leading-tight break-words">{input1}</div>
            {input2 && (
              <div className="text-xl font-medium text-zinc-300 mt-2 leading-tight break-words">{input2}</div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  if (!studioId || loading || !settings) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center transition-colors duration-1000 ease-in-out">
        <span className="text-zinc-800 text-sm font-mono">Awaiting Studio ID...</span>
      </div>
    );
  }

  const leftType = settings.left_card_type || 'Wi-Fi';
  const rightType = settings.right_card_type || 'QR Code';
  const hasLeftCard = leftType !== 'None';
  const hasRightCard = rightType !== 'None';
  const hasAnyCard = hasLeftCard || hasRightCard;

  return (
    <div
      className="w-screen h-screen overflow-hidden flex flex-col relative bg-zinc-950 text-white selection:bg-zinc-800 cursor-default transition-colors duration-1000 ease-in-out"
      onDoubleClick={handleDoubleTap}
    >
      <div className="absolute top-8 w-full px-12 flex justify-between items-start gap-6 z-10">
        <div className="flex-1 flex justify-start">
          {settings.show_name && settings.studio_name?.trim() && (
            <div className="px-6 py-3 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 flex items-center">
              <div className="text-white/60 font-semibold tracking-widest uppercase text-[1.2vmax] max-w-[40vw] text-left leading-snug break-words line-clamp-2 transition-opacity duration-700 ease-in-out">
                {settings.studio_name}
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 flex justify-end">
          {settings.show_clock && (
            <div className="px-6 py-3 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 flex items-center">
              <div className="text-white/60 font-semibold tracking-widest uppercase text-[1.2vmax] whitespace-nowrap transition-opacity duration-700 ease-in-out">
                {timeString}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={`absolute inset-0 flex flex-col justify-center items-center text-center z-0 px-12 transition-all duration-1000 ease-in-out ${hasAnyCard ? '-mt-20' : ''}`}>
        {settings.lobby_welcome ? (
          <h1 className="font-extrabold tracking-tighter text-white text-[8vmax] leading-none max-w-[90vw] break-words transition-opacity duration-700 ease-in-out">
            {settings.lobby_welcome}
          </h1>
        ) : null}
        {settings.lobby_subtext ? (
          <p className={`text-zinc-200 font-medium text-[2.5vmax] leading-tight max-w-[80vw] break-words transition-opacity duration-700 ease-in-out ${settings.lobby_welcome ? 'mt-6' : ''}`}>
            {settings.lobby_subtext}
          </p>
        ) : null}
      </div>

      {hasAnyCard && (
        <div className="absolute bottom-16 w-full flex justify-center items-stretch gap-8 px-12 z-10 transition-opacity duration-700 ease-in-out">
          {renderCard(leftType, settings.left_card_input_1, settings.left_card_input_2)}
          {renderCard(rightType, settings.right_card_input_1, settings.right_card_input_2)}
        </div>
      )}
    </div>
  );
}

export default function LobbyDisplay() {
  return (
    <Suspense fallback={
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center">
        <span className="text-zinc-800 text-sm font-mono">Awaiting Studio ID...</span>
      </div>
    }>
      <LobbyDisplayContent />
    </Suspense>
  );
}